'use client';

/**
 * ComposerSurface — registry-driven composer panel.
 *
 * Third peer surface in the right rail (alongside Insights and
 * Copilot), one visible at a time, all always mounted. Renders a
 * 1×N stack of cards keyed by the active mode's
 * `composer_surface_cards` matrix field. Mounted regardless of
 * matrix eligibility — when the active mode doesn't declare
 * composer cards, the surface paints an empty state and the
 * EdgeStrip cart icon falls back to the legacy floating drawer for
 * that mode.
 *
 * The data layer (`useCartStore`, persisted as `klyde-cart-v4`) is
 * the late-binding-intent design the architecture doc's Composer
 * section describes — already shipped. This component is the
 * cards-everywhere paint: a sibling rail surface, not an overlay.
 *
 * @see klyde_edge/composition/cards/composer.py
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @component_role Sibling rail surface
 * @component_type Panel
 * @domain Frontend Shell / Procurement
 * @layer presentation
 */

import { ShoppingCart } from 'lucide-react';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { COMPOSER_CARD_REGISTRY } from './composer-card-registry';
import { CardChromeProvider } from '@/lib/design/card-chrome';

export function ComposerSurface() {
  const { mode } = useViewportSelection();
  const getModeConfig = useTotalMatrixStore((s) => s.getModeConfig);
  const cardKeys = getModeConfig(mode)?.composer_surface_cards ?? [];

  if (cardKeys.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-panel text-muted-foreground p-6">
        <ShoppingCart className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">Composer not available in this mode</p>
        <p className="text-xs mt-1 text-center">
          The cart drawer is still floating here — switch to a mode that has
          opted in to see the rail-stacked Composer.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-panel">
      <div className="p-2 space-y-2">
        {cardKeys.map((key) => {
          const Component = COMPOSER_CARD_REGISTRY[key];
          if (!Component) {
            if (typeof window !== 'undefined') {
              // eslint-disable-next-line no-console
              console.warn(
                `[ComposerSurface] Unknown card key '${key}' — add to COMPOSER_CARD_REGISTRY`,
              );
            }
            return null;
          }
          // Give each composer card the chrome seam so its typography
          // resolves through cardTypography[content_key] (→ the migration
          // map), same as canvas/rail/explorer. Cards read it via
          // useCardTypography(); if the map lacks an entry the alarm fires
          // (mono/fuchsia) rather than a silent fallback.
          return (
            <CardChromeProvider key={key} contentKey={key}>
              <Component />
            </CardChromeProvider>
          );
        })}
      </div>
    </div>
  );
}
